import { useCallback, useEffect, useState } from 'react';
import {
  forecastEngineApi,
  type ActivityEntry,
  type AlgorithmConfig,
  type ForecastAccuracy,
  type ForecastComparison,
  type ForecastDashboard,
  type ForecastModel,
  type ForecastRecord,
  type ForecastScenario,
  type ForecastSignal,
} from '../../api/forecastengine';

type Tab = 'dashboard' | 'algorithms' | 'accuracy' | 'signals' | 'scenarios' | 'activity';
type Theme = 'light' | 'dark';

function algoLabel(algo: string) {
  return algo.replace(/_/g, ' ');
}

function fmtNum(n: number) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

export function ForecastEnginePage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('dashboard');
  const [dashboard, setDashboard] = useState<ForecastDashboard | null>(null);
  const [forecast, setForecast] = useState<ForecastRecord | null>(null);
  const [models, setModels] = useState<ForecastModel[]>([]);
  const [comparison, setComparison] = useState<ForecastComparison | null>(null);
  const [accuracies, setAccuracies] = useState<ForecastAccuracy[]>([]);
  const [signals, setSignals] = useState<ForecastSignal | null>(null);
  const [scenarios, setScenarios] = useState<ForecastScenario[]>([]);
  const [algorithms, setAlgorithms] = useState<AlgorithmConfig[]>([]);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, forecasts, algos, act] = await Promise.all([
        forecastEngineApi.dashboard(),
        forecastEngineApi.listForecasts(),
        forecastEngineApi.listAlgorithms(),
        forecastEngineApi.listActivity(),
      ]);
      setDashboard(dash);
      setAlgorithms(algos);
      setActivity(act);
      const fc = forecasts[0] ?? null;
      setForecast(fc);
      if (!fc) return;

      const [mods, cmp, acc, sig, sc] = await Promise.all([
        forecastEngineApi.listModels(fc.id),
        forecastEngineApi.getComparison(fc.id),
        forecastEngineApi.listAccuracies(fc.id),
        forecastEngineApi.getSignals(fc.id),
        forecastEngineApi.listScenarios(fc.id),
      ]);
      setModels(mods);
      setComparison(cmp);
      setAccuracies(acc);
      setSignals(sig);
      setScenarios(sc);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load forecast engine');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const shell = dark ? 'min-h-screen bg-slate-950 text-slate-100' : 'min-h-screen bg-slate-50 text-slate-900';
  const card = dark ? 'rounded-xl border border-slate-800 bg-slate-900' : 'rounded-xl border border-slate-200 bg-white shadow-sm';
  const header = dark ? 'border-b border-slate-800 bg-slate-900' : 'border-b border-slate-200 bg-white';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'algorithms', label: 'Algorithm Comparison' },
    { id: 'accuracy', label: 'Accuracy' },
    { id: 'signals', label: 'Signal Analysis' },
    { id: 'scenarios', label: 'Scenarios' },
    { id: 'activity', label: 'Activity' },
  ];

  return (
    <div className={shell}>
      <div className={`${header} px-6 py-4`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Statistical & Sales Forecast Engine</h1>
            <p className={`mt-1 text-sm ${dark ? 'text-slate-400' : 'text-slate-600'}`}>
              Multi-algorithm statistical forecasting — SMA, Holt-Winters, Croston, ensemble selection
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setTheme(dark ? 'light' : 'dark')}
              className={`rounded-lg px-3 py-2 text-sm ${dark ? 'bg-slate-800' : 'bg-slate-100'}`}
              aria-label="Toggle dark mode"
            >
              {dark ? 'Light' : 'Dark'}
            </button>
            <button
              type="button"
              onClick={refresh}
              disabled={busy}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              Refresh
            </button>
          </div>
        </div>
        {forecast && (
          <p className="mt-2 text-sm text-slate-500">
            {forecast.name} · {forecast.horizon.replace('_', ' ')} ·{' '}
            Selected: {algoLabel(forecast.selected_algorithm ?? '—')} · Confidence {(forecast.confidence * 100).toFixed(0)}%
          </p>
        )}
      </div>

      {error && (
        <div className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
          {error}
        </div>
      )}

      <div className={`${header} px-6`}>
        <nav className="flex gap-1 overflow-x-auto" role="tablist">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium ${
                tab === t.id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="p-6">
        {tab === 'dashboard' && dashboard && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
              {[
                { label: 'Active Forecasts', value: dashboard.active_forecasts },
                { label: 'Avg MAPE', value: `${dashboard.avg_mape.toFixed(1)}%` },
                { label: 'Avg Bias', value: `${dashboard.avg_bias.toFixed(1)}%` },
                { label: 'Algorithms Run', value: dashboard.algorithms_run },
                { label: 'Published', value: dashboard.published_forecasts },
                { label: 'Variance Alerts', value: dashboard.variance_alerts },
              ].map((k) => (
                <div key={k.label} className={`${card} p-4`}>
                  <p className="text-xs uppercase text-slate-500">{k.label}</p>
                  <p className="mt-1 text-xl font-semibold">{k.value}</p>
                </div>
              ))}
            </div>
            {forecast && (
              <div className={`${card} p-6`}>
                <h2 className="text-lg font-semibold">Selected Forecast</h2>
                <div className="mt-4 grid gap-4 md:grid-cols-3">
                  <div>
                    <p className="text-sm text-slate-500">Forecast Units</p>
                    <p className="text-2xl font-semibold">{fmtNum(forecast.forecast_units)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Status</p>
                    <p className="font-medium capitalize">{forecast.status}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Algorithm</p>
                    <p className="font-medium">{algoLabel(forecast.selected_algorithm ?? '—')}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'algorithms' && (
          <div className={`${card} overflow-x-auto p-6`}>
            <h2 className="text-lg font-semibold">Algorithm Comparison</h2>
            <table className="mt-4 min-w-full text-sm">
              <thead>
                <tr className={`border-b ${dark ? 'border-slate-700 text-slate-400' : 'border-slate-200 text-slate-500'}`}>
                  <th className="pb-2 text-left">Algorithm</th>
                  <th className="pb-2 text-left">MAPE</th>
                  <th className="pb-2 text-left">MAE</th>
                  <th className="pb-2 text-left">Bias</th>
                  <th className="pb-2 text-left">Stability</th>
                  <th className="pb-2 text-left">Selected</th>
                </tr>
              </thead>
              <tbody>
                {models.map((m) => (
                  <tr key={m.id} className={`border-b ${dark ? 'border-slate-800' : 'border-slate-100'}`}>
                    <td className="py-2">
                      <div className="font-medium capitalize">{algoLabel(m.algorithm)}</div>
                      <div className="text-xs text-slate-500">{m.explainability}</div>
                    </td>
                    <td className="py-2">{m.accuracy_mape.toFixed(1)}%</td>
                    <td className="py-2">{m.accuracy_mae.toFixed(0)}</td>
                    <td className="py-2">{m.bias.toFixed(1)}%</td>
                    <td className="py-2">{(m.stability * 100).toFixed(0)}%</td>
                    <td className="py-2">{m.selected ? '✓' : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'accuracy' && (
          <div className={`${card} p-6`}>
            <h2 className="text-lg font-semibold">Forecast Accuracy</h2>
            {accuracies.map((a) => (
              <div key={a.id} className="mt-4 grid gap-3 md:grid-cols-4">
                {[
                  { label: 'MAPE', value: `${a.mape.toFixed(1)}%` },
                  { label: 'WMAPE', value: `${a.wmape.toFixed(1)}%` },
                  { label: 'RMSE', value: a.rmse.toFixed(0) },
                  { label: 'FVA', value: a.fva.toFixed(1) },
                  { label: 'Bias', value: `${a.bias.toFixed(1)}%` },
                  { label: 'Tracking Signal', value: a.tracking_signal.toFixed(2) },
                  { label: 'Reliability', value: `${(a.reliability * 100).toFixed(0)}%` },
                  { label: 'Historical Accuracy', value: `${a.historical_accuracy.toFixed(1)}%` },
                ].map((m) => (
                  <div key={m.label} className={`rounded-lg p-3 ${dark ? 'bg-slate-800' : 'bg-slate-50'}`}>
                    <p className="text-xs text-slate-500">{m.label}</p>
                    <p className="font-semibold">{m.value}</p>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {tab === 'signals' && signals && (
          <div className={`${card} p-6`}>
            <h2 className="text-lg font-semibold">Demand Signal Analysis</h2>
            <div className="mt-4 grid gap-4 md:grid-cols-3">
              {[
                { label: 'Trend', value: signals.trend.toFixed(2) },
                { label: 'Seasonality', value: `${(signals.seasonality * 100).toFixed(0)}%` },
                { label: 'Growth', value: `${signals.growth.toFixed(1)}%` },
                { label: 'Volatility', value: `${(signals.volatility * 100).toFixed(0)}%` },
                { label: 'Outliers', value: signals.outlier_count },
                { label: 'Lifecycle', value: signals.lifecycle_stage.replace('_', ' ') },
                { label: 'Signal Quality', value: `${(signals.signal_quality * 100).toFixed(0)}%` },
                { label: 'Confidence', value: `${(signals.signal_confidence * 100).toFixed(0)}%` },
                { label: 'Promotion Impact', value: `${((signals.promotion_impact ?? 0) * 100).toFixed(0)}%` },
              ].map((s) => (
                <div key={s.label} className={`rounded-lg p-3 ${dark ? 'bg-slate-800' : 'bg-slate-50'}`}>
                  <p className="text-xs text-slate-500">{s.label}</p>
                  <p className="font-semibold capitalize">{s.value}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === 'scenarios' && (
          <div className="grid gap-4 md:grid-cols-3">
            {scenarios.map((sc) => (
              <div key={sc.id} className={`${card} p-4`}>
                <h3 className="font-medium">{sc.name}</h3>
                <p className="text-sm capitalize text-slate-500">{sc.type.replace('_', ' ')}</p>
                <p className="mt-2 text-lg font-semibold">{fmtNum(sc.forecast_units)} units</p>
                <p className="text-sm text-indigo-500">{sc.growth_pct > 0 ? '+' : ''}{sc.growth_pct}% growth</p>
              </div>
            ))}
          </div>
        )}

        {tab === 'activity' && (
          <div className="space-y-2">
            {activity.map((a) => (
              <div key={a.id} className={`${card} px-4 py-3 text-sm`}>
                <span className="font-medium">{a.action}</span> — {a.message}
                <span className="ml-2 text-xs text-slate-500">{new Date(a.created_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}

        {tab === 'dashboard' && algorithms.length > 0 && (
          <div className={`${card} mt-6 p-6`}>
            <h2 className="text-lg font-semibold">Enabled Algorithms</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {algorithms.filter((a) => a.enabled).map((a) => (
                <span key={a.algorithm} className="rounded-full bg-indigo-100 px-3 py-1 text-xs text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300">
                  {algoLabel(a.algorithm)}
                </span>
              ))}
            </div>
            <p className="mt-3 text-xs text-slate-500">
              AI forecasting (DEV-000066) will augment these statistical outputs — not replace them.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
